/**
 * Pathway Specialism Utilities
 *
 * Clinicians can be assigned to one or more assessment pathways (Adult ADHD,
 * Adult Autism, CYP ADHD, etc.) via the `pathwaySpecialisms` array on their
 * clinician document. These utilities enforce pathway-based filtering in
 * assignment UIs, ensuring a clinician can only be assigned to patients whose
 * assessment module matches one of their specialisms.
 *
 * Backwards compatibility: if a clinician's `pathwaySpecialisms` field is null,
 * undefined, or an empty array, they are treated as having `['adultAdhd']` only.
 * This avoids requiring a backfill migration for existing clinician accounts that
 * were created before this feature was introduced.
 *
 * Keys and labels come from the assessment-module registry so adding a pathway
 * there (and enabling it on the org) is enough for staff setup/edit checkboxes
 * to offer it — we do not keep a second hardcoded list here.
 */

import { ASSESSMENT_MODULES } from '../constants/assessmentModules';

/**
 * The canonical set of valid pathway specialism values.
 * These must match the `assessmentModule` field values on patient documents
 * and the allowed set in the backend Cloud Functions.
 */
export const VALID_PATHWAY_SPECIALISMS = Object.freeze(Object.keys(ASSESSMENT_MODULES));

/**
 * Human-readable labels for each pathway specialism, used in dropdowns,
 * badges, and staff table columns.
 */
export const PATHWAY_SPECIALISM_LABELS = Object.freeze(
  Object.fromEntries(
    Object.entries(ASSESSMENT_MODULES).map(([key, cfg]) => [key, cfg.label]),
  ),
);

/**
 * Pathways an admin may tick for this organisation.
 *
 * Which pathways appear is data-driven: the org's Firestore modules doc lists
 * the enabled assessment types (e.g. adultAdhd, cypAdhd). When org modules have not loaded yet we
 * fall back to the full known set so the selector is never silently limited
 * to Adult ADHD. Currently-selected values stay visible even if the org later
 * drops that module, so an admin can still uncheck a stale assignment.
 *
 * @param {string[]|null|undefined} availableOrgModules
 * @param {string[]} [currentlySelected]
 * @returns {string[]}
 */
export function getSelectablePathwaySpecialisms(availableOrgModules, currentlySelected = []) {
  const selected = Array.isArray(currentlySelected) ? currentlySelected : [];
  if (!Array.isArray(availableOrgModules) || availableOrgModules.length === 0) {
    return [...VALID_PATHWAY_SPECIALISMS];
  }
  const enabled = new Set(availableOrgModules);
  return VALID_PATHWAY_SPECIALISMS.filter(
    (specialism) => enabled.has(specialism) || selected.includes(specialism),
  );
}

/**
 * Returns the effective pathway specialisms for a clinician, applying the
 * backwards-compatibility default of ['adultAdhd'] when the field is missing.
 *
 * @param {Object|null} clinicianData - The clinician document data
 * @returns {string[]} - The clinician's effective pathway specialisms
 */
export function getEffectiveSpecialisms(clinicianData) {
  const specialisms = clinicianData?.pathwaySpecialisms;
  if (!Array.isArray(specialisms) || specialisms.length === 0) {
    return ['adultAdhd'];
  }
  return specialisms;
}

/**
 * Checks whether a clinician can be assigned to a patient on a given pathway.
 *
 * @param {Object|null} clinicianData - The clinician document data (must include
 *   `pathwaySpecialisms` if present)
 * @param {string|null} assessmentModule - The patient's assessment module
 *   (e.g. 'adultAdhd', 'adultAutism', 'cypAdhd')
 * @returns {boolean} - True if the clinician's specialisms include the patient's
 *   assessment module, or if assessmentModule is falsy (permissive fallback)
 */
export function canClinicianBeAssignedToPathway(clinicianData, assessmentModule) {
  if (!assessmentModule) return true;
  const specialisms = getEffectiveSpecialisms(clinicianData);
  return specialisms.includes(assessmentModule);
}
